import { zodResolver } from '@hookform/resolvers/zod';
import { BusinessSegment, PartnerSegment, TypeOfBusiness } from 'modules/data/domain';
import { updateProfileData } from 'modules/first-start/actions';
import { useEffect } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { useDispatch } from 'react-redux';
import { amplitudeService } from 'services/amplitude';

import { fields, largeBusinessByNumberOfEntities, largePartnerByStatus } from '../../../resources/constants';
import { FirstStartProfileData } from '../../types';
import { getSchema } from './ProfileSetupForm.schema';
import { ProfileSetupFormProps } from './ProfileSetupForm.types';

export const useProfileSetupFormContext = () => {
    return useFormContext<FirstStartProfileData>();
};

export const useProfileSetupForm = (props: ProfileSetupFormProps) => {
    const dispatch = useDispatch();
    const { isLoading, showPhoneField, showPasswordField, showConsent, showQuestions, onSubmit, profileData } = props;

    const form = useForm<FirstStartProfileData>({
        shouldFocusError: false,
        defaultValues: {
            [fields.fieldNameFirstName]: profileData.firstName ?? '',
            [fields.fieldNameLastName]: profileData.lastName ?? '',
            [fields.fieldNamePhone]: showPhoneField ? profileData.phone ?? '' : null,
            [fields.fieldNamePassword]: showPasswordField ? '' : null,
            [fields.fieldNameTypeOfBusiness]: null,
            [fields.fieldNamePartnerStatus]: null,
            [fields.fieldNameProductInterest]: null,
            [fields.fieldNameNumberOfEntities]: null,
            [fields.fieldNameNumberOfClients]: null,
            [fields.fieldNameUseCase]: null,
            [fields.fieldNamePartnerSegment]: null,
            [fields.fieldNameBusinessSegment]: null,
            [fields.fieldNameConsentMarketingAccepted]: false,
        },
        resolver: zodResolver(getSchema({ showPasswordField, showPhoneField, showConsent, showQuestions })),
    });

    const watchedFields = form.watch();
    const watchedFieldPhone = watchedFields[fields.fieldNamePhone];

    useEffect(() => {
        if (watchedFields.firstName || watchedFields.lastName || watchedFields.phone || watchedFields.password) {
            dispatch(
                updateProfileData({
                    firstName: watchedFields.firstName,
                    lastName: watchedFields.lastName,
                    phone: watchedFields.phone,
                    password: watchedFields.password ?? '',
                })
            );
        }
    }, [dispatch, watchedFields.firstName, watchedFields.lastName, watchedFields.password, watchedFields.phone]);

    const onFormSubmit = form.handleSubmit((data) => {
        if (isLoading) {
            return;
        }

        onSubmit(data);
    });

    useEffect(() => {
        if (
            fields.fieldNamePhone in form.formState.errors &&
            typeof watchedFieldPhone === 'string' &&
            !isPossiblePhoneNumber(watchedFieldPhone)
        ) {
            amplitudeService.sendData('signup: error in phone validation occurred', { value: watchedFieldPhone });
        }
    }, [form.formState.errors, watchedFieldPhone]);

    useEffect(() => {
        if (watchedFields.typeOfBusiness !== null) {
            amplitudeService.sendData('signup: chose the type of company');
        }
    }, [watchedFields.typeOfBusiness]);

    useEffect(() => {
        switch (watchedFields.typeOfBusiness) {
            case TypeOfBusiness.Partner:
                form.setValue(
                    fields.fieldNamePartnerSegment,
                    watchedFields.partnerStatus && largePartnerByStatus.has(watchedFields.partnerStatus)
                        ? PartnerSegment.LargePartner
                        : PartnerSegment.SmallPartner
                );
                break;

            case TypeOfBusiness.Business:
                form.setValue(
                    fields.fieldNameBusinessSegment,
                    watchedFields.numberOfEntities &&
                        largeBusinessByNumberOfEntities.has(watchedFields.numberOfEntities)
                        ? BusinessSegment.ManagedBusiness
                        : BusinessSegment.UnmanagedBusiness
                );
                break;

            default:
                form.setValue(fields.fieldNamePartnerSegment, null);
                form.setValue(fields.fieldNameBusinessSegment, null);
        }
    }, [
        form,
        form.setValue,
        watchedFields.numberOfEntities,
        watchedFields.partnerStatus,
        watchedFields.typeOfBusiness,
    ]);

    return {
        form,
        onFormSubmit,
    };
};
